/**
 * 把真实数据库 schema 整理成「可直接粘进 LLM prompt」的紧凑上下文。
 *
 * 问题背景：LLM 首次生成报表 SQL 时，往往凭空猜测表名/字段名
 * （如把 employee 表猜成 t_emp、把 EMPLOYEE_NAME 猜成 name），
 * 直到 generate 校验才暴露「构建字段失败」。本模块让「写 SQL 之前」
 * 先把真实表结构注入 prompt，从源头避免编造表名/列名。
 *
 * 设计取舍：
 *  - 紧凑优先：每行 `表名(列:类型, ...)`，远省上下文 token。
 *  - 大小写保持服务端原样（如表名 EMPLOYEE），因为 SQL 标识符大小写敏感与否
 *    取决于底层库，照原样写入最安全。
 *  - 失败表单独列出，避免污染可用 schema 上下文。
 */

import type { TableSchema } from '../../client/datasourceApi.js';

export interface PromptSchemaOptions {
  /** 仅保留这些表的子集（按原名或忽略大小写匹配）。缺省保留全部成功表。 */
  tables?: string[];
  /** 单表字段超过该数量时，提示「按需裁剪」而非全列（默认 40）。 */
  maxColumnsPerTable?: number;
}

/** 大小写不敏感匹配表名。 */
function matchTables(all: TableSchema[], want?: string[]): TableSchema[] {
  if (!want || want.length === 0) return all;
  const byLower = new Map(all.map((t) => [t.name.toLowerCase(), t]));
  const out: TableSchema[] = [];
  const seen = new Set<string>();
  for (const raw of want) {
    const hit = byLower.get(raw.toLowerCase());
    if (hit && !seen.has(hit.name)) {
      seen.add(hit.name);
      out.push(hit);
    }
  }
  return out;
}

/** 生成可粘贴进 prompt 的 schema 文本块。 */
export function buildPromptSchema(
  schema: TableSchema[],
  opts: PromptSchemaOptions = {},
): string {
  const maxCols = opts.maxColumnsPerTable ?? 40;
  const ok = matchTables(schema, opts.tables).filter((t) => !t.error && t.columns.length);
  const failed = schema.filter((t) => t.error);

  const lines: string[] = ['# 数据库真实表结构（写 SQL 必须严格使用下列名称，禁止臆造表名/字段名）'];
  for (const t of ok) {
    const cols = t.columns.map((c) => `${c.name}:${c.type ?? '?'}`).join(', ');
    const note = t.columns.length > maxCols ? `  /* ${t.columns.length} 列，按需裁剪 */` : '';
    lines.push(`- ${t.name}(${cols})${note}`);
  }
  if (failed.length) {
    lines.push('');
    lines.push('/* 以下表探查失败，本批次不可用： */');
    for (const t of failed) lines.push(`- ${t.name}: ${t.error}`);
  }
  return lines.join('\n');
}

/**
 * 当 SQL 校验失败时，从真实 schema 中挑出「与出错 SQL 最可能相关」的表/字段建议，
 * 帮助 LLM 自动修正而非盲目重试。
 *
 * 策略（轻量、无需额外网络调用，仅基于已拿到的 schema）：
 *  - 表候选：以 SQL 中出现的标识符（FROM/JOIN 后的词）为种子，按子串/词根匹配真实表名。
 *  - 字段候选：以声明字段名为种子，在候选表字段中按子串匹配给出「你是不是想写 X」。
 */
export function suggestFromSchema(
  schema: TableSchema[],
  seedTables: string[],
  seedFields: string[],
): { table?: string[]; fields: Record<string, string[]> } {
  const ok = schema.filter((t) => !t.error && t.columns.length);
  const lowerTables = ok.map((t) => t.name.toLowerCase());

  const tableSuggest: string[] = [];
  for (const seed of seedTables.filter((s): s is string => !!s)) {
    const s = seed.toLowerCase();
    const stem = s.replace(/^(t|tb|tbl|tab)[_-]?/, '');
    const hits = ok
      .filter((t) => {
        const a = t.name.toLowerCase();
        return a.includes(s) || s.includes(a) || (stem.length >= 3 && (a.includes(stem) || stem.includes(a)));
      })
      .map((t) => t.name)
      .slice(0, 5);
    if (hits.length && !hits.some((h) => tableSuggest.includes(h))) tableSuggest.push(...hits);
  }

  // 字段建议：在候选表（或所有表）的字段里，按子串/词根找近似。
  const fieldsOut: Record<string, string[]> = {};
  const pool = (tableSuggest.length ? ok.filter((t) => tableSuggest.includes(t.name)) : ok).flatMap(
    (t) => t.columns.map((c) => c.name),
  );
  const poolLower = pool.map((p) => p.toLowerCase());
  for (const f of seedFields) {
    const s = f.toLowerCase();
    const stem = s.replace(/^(t|tb|tbl|tab|f_|col_)[_-]?/, '');
    const near: string[] = [];
    poolLower.forEach((p, i) => {
      const col = pool[i];
      if (col === undefined) return;
      if (p === s) return;
      if (p.includes(s) || s.includes(p) || (stem.length >= 3 && (p.includes(stem) || stem.includes(p)))) {
        if (!near.includes(col)) near.push(col);
      }
    });
    if (near.length) fieldsOut[f] = near.slice(0, 5);
  }
  return { table: tableSuggest, fields: fieldsOut };
}
